import React, { useState, useEffect } from "react"
import { Card, Modal } from "reactstrap"
import Loader from "pages/Separate/Loader"
import { fetchNodeData } from "utils/common-fetchData"
import Swal from "sweetalert2"
import secureLocalStorage from "react-secure-storage"
import moment from "moment"
import configure from "configure"
import { formatDateTime } from "utils/formatDate"

const PASS_SCORE = 70
const EXCELLENCE_SCORE = 90
const EXCELLENCE_STREAK_REQUIRED = 4

const _TOKEN_SECRET = 1513948082
const _USER_KEY = "zt9x2k7r"

function encodeUserData(name, email) {
  const json = JSON.stringify({ name, email })
  let xored = ""
  for (let i = 0; i < json.length; i++) {
    xored += String.fromCharCode(json.charCodeAt(i) ^ _USER_KEY.charCodeAt(i % _USER_KEY.length))
  }
  return btoa(xored).replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "")
}

function encodeTestId(id) {
  const xored = (Number(id) ^ _TOKEN_SECRET) >>> 0
  const hexPart = xored.toString(16).padStart(8, "0")
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789"
  const randPart = Array.from({ length: 8 }, () =>
    chars[Math.floor(Math.random() * chars.length)]
  ).join("")
  return randPart + hexPart
}

function scoreColor(score) {
  if (score >= EXCELLENCE_SCORE) return "#16a34a"
  if (score >= PASS_SCORE) return "#d97706"
  return "#dc2626"
}
function scoreBg(score) {
  if (score >= EXCELLENCE_SCORE) return "#f0fdf4"
  if (score >= PASS_SCORE) return "#fffbeb"
  return "#fff5f5"
}
function scoreBorder(score) {
  if (score >= EXCELLENCE_SCORE) return "#86efac"
  if (score >= PASS_SCORE) return "#fde68a"
  return "#fecaca"
}
function ringColor(score) {
  if (score >= EXCELLENCE_SCORE) return "#27ae60"
  if (score >= PASS_SCORE) return "#f39c12"
  return "#e74c3c"
}
function scoreLeftBorder(score) {
  if (score >= EXCELLENCE_SCORE) return "#22c55e"
  if (score >= PASS_SCORE) return "#f59e0b"
  return "#ef4444"
}

function computeExcellenceStreak(data) {
  // All submissions across all tests, sorted newest first
  const allSubmissions = data
    .flatMap(item => (item.submissions ?? []).map(sub => ({
      score: sub.score ?? 0,
      date: sub.created_at,
    })))
    .sort((a, b) => new Date(b.date) - new Date(a.date))

  let streak = 0
  for (const s of allSubmissions) {
    if (s.score >= EXCELLENCE_SCORE) streak++
    else break
  }
  return { streak, total: allSubmissions.length }
}

const MyAssessment = () => {
  const [isLoading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [resultModal, setResultModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [activeTab, setActiveTab] = useState("actual")

  const toggleLoading = v => setLoading(v)

  useEffect(() => {
    const load = async () => {
      const token = secureLocalStorage.getItem("auth_token")
      const userId = localStorage.getItem("auth_id")
      const email = secureLocalStorage.getItem("auth_email")
      if (!userId) return
      const query = email ? `?email=${encodeURIComponent(email)}` : ""
      const res = await fetchNodeData(
        toggleLoading,
        `preassesment/test/user/${userId}${query}`,
        token
      )
      if (res?.success) setData(res.data)
    }
    load()
  }, [])

  const openResult = item => {
    setSelectedItem(item)
    setResultModal(true)
  }

  const actualData = data.filter(item => item.test?.test_type == 0)
  const mockData = data.filter(item => item.test?.test_type == 1)
  const tabData = activeTab === "actual" ? actualData : mockData

  const { streak, total } = computeExcellenceStreak(actualData)
  const excellenceComplete = streak >= EXCELLENCE_STREAK_REQUIRED

  return (
    <div>
      {isLoading && <Loader />}
      <Card>
        <div className="card-body">
          {/* Header */}
          <div
            className="d-flex align-items-center justify-content-between mb-3 pb-2"
            style={{ borderBottom: "1.5px solid #f0e6f8" }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: "linear-gradient(135deg, #752a97, #a855c9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i className="fas fa-clipboard-list text-white" style={{ fontSize: 15 }}></i>
              </div>
              <span className="text-purple fw-bold" style={{ fontSize: 15 }}>My Assessment</span>
            </div>
            {data.length > 0 && (
              <span
                style={{
                  background: "#f3e8ff",
                  color: "#752a97",
                  border: "1px solid #d8b4fe",
                  borderRadius: 20,
                  padding: "3px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {data.length} Test{data.length !== 1 ? "s" : ""} Assigned
              </span>
            )}
          </div>

          {/* Tabs */}
          <div className="d-flex gap-2 mb-3">
            {[
              { key: "actual", label: "Actual Test", count: actualData.length },
              { key: "mock", label: "Mock Test", count: mockData.length },
            ].map(tab => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: "5px 16px",
                  borderRadius: 20,
                  border: activeTab === tab.key ? "none" : "1px solid #d8b4fe",
                  background: activeTab === tab.key
                    ? "linear-gradient(135deg, #752a97, #a855c9)"
                    : "transparent",
                  color: activeTab === tab.key ? "#fff" : "#752a97",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {tab.label}
                <span
                  style={{
                    background: activeTab === tab.key ? "rgba(255,255,255,0.3)" : "#f3e8ff",
                    color: activeTab === tab.key ? "#fff" : "#752a97",
                    borderRadius: 10,
                    padding: "1px 7px",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Excellence streak banner — actual tests only */}
          {activeTab === "actual" && total > 0 && (
            <div
              className="mb-3 p-3"
              style={{
                borderRadius: 10,
                background: excellenceComplete ? "linear-gradient(135deg, #f0fdf4, #dcfce7)" : "#faf5ff",
                border: `1px solid ${excellenceComplete ? "#86efac" : "#e9d5ff"}`,
              }}
            >
              {excellenceComplete ? (
                <div className="d-flex align-items-center gap-2">
                  <i className="fas fa-trophy" style={{ fontSize: 18, color: "#16a34a" }}></i>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#15803d" }}>
                      Excellence Achieved!
                    </div>
                    <div style={{ fontSize: 12, color: "#166534" }}>
                      You scored 90%+ in {EXCELLENCE_STREAK_REQUIRED} consecutive assessments. No further training required.
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <span style={{ fontSize: 12, fontWeight: 600, color: "#752a97" }}>
                      <i className="fas fa-star me-1"></i>Excellence Streak
                    </span>
                    <span style={{ fontSize: 12, color: "#555", fontWeight: 600 }}>
                      {streak} / {EXCELLENCE_STREAK_REQUIRED}
                    </span>
                  </div>
                  <div style={{ height: 7, background: "#e9d5ff", borderRadius: 10, overflow: "hidden" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${(streak / EXCELLENCE_STREAK_REQUIRED) * 100}%`,
                        background: "linear-gradient(90deg, #752a97, #a855c9)",
                        borderRadius: 10,
                        transition: "width 0.4s ease",
                      }}
                    />
                  </div>
                  <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                    Score 90%+ in {EXCELLENCE_STREAK_REQUIRED - streak} more consecutive assessment{EXCELLENCE_STREAK_REQUIRED - streak !== 1 ? "s" : ""} to complete training
                  </div>
                </div>
              )}
            </div>
          )}

          {tabData.length === 0 && !isLoading ? (
            <div className="text-center text-muted py-4">
              <i className="fas fa-clipboard-list fa-2x mb-2 d-block"></i>
              No {activeTab === "actual" ? "actual" : "mock"} assessments assigned to you.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 40 }}>#</th>
                    <th>Test Name</th>
                    <th className="text-center" style={{ width: 110 }}>High Score</th>
                    <th className="text-center" style={{ width: 110 }}>Avg Score</th>
                    <th className="text-center" style={{ width: 200 }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tabData.map((item, i) => {
                    const { test, submissions } = item
                    const { id } = test
                    const authName = secureLocalStorage.getItem("auth_name") || ""
                    const authEmail = secureLocalStorage.getItem("auth_email") || ""
                    const examUrl = `${window.location.origin}${configure.appUrl}assessment-exam/${encodeTestId(id)}?d=${encodeUserData(authName, authEmail)}`
                    const notExpired = !test.expiry || moment(test.expiry, "YYYY-MM-DD").endOf("day").isAfter(moment())
                    const isActual = test.test_type == 0
                    const hasSubmittedToday = isActual && submissions?.some(
                      sub => moment(sub.created_at).isSame(moment(), "day")
                    )
                    const highScore = submissions?.length > 0
                      ? Math.max(...submissions.map(s => s.score ?? 0))
                      : null
                    const avgScore = submissions?.length > 0
                      ? Math.round(submissions.reduce((sum, s) => sum + (s.score ?? 0), 0) / submissions.length)
                      : null
                    return (
                      <tr key={test.id}>
                        <td>{i + 1}</td>
                        <td>{test.test_name}</td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          {highScore != null ? (
                            <span style={{ fontWeight: 700, fontSize: 13, color: scoreColor(highScore) }}>
                              {highScore}%
                            </span>
                          ) : <span style={{ color: "#d1d5db", fontSize: 13 }}>—</span>}
                        </td>
                        <td className="text-center" style={{ verticalAlign: "middle" }}>
                          {avgScore != null ? (
                            <span style={{ fontWeight: 700, fontSize: 13, color: scoreColor(avgScore) }}>
                              {avgScore}%
                            </span>
                          ) : <span style={{ color: "#d1d5db", fontSize: 13 }}>—</span>}
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center align-items-center gap-2">
                            {notExpired && (
                              <a
                                href={hasSubmittedToday ? undefined : examUrl}
                                target={hasSubmittedToday ? undefined : "_blank"}
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-outline-primary"
                                onClick={hasSubmittedToday ? e => {
                                  e.preventDefault()
                                  Swal.fire({
                                    icon: "info",
                                    title: "Daily Limit Reached",
                                    text: "You can submit only one response per day for actual tests. Please try again on next schedule Day.",
                                    confirmButtonColor: "#752a97",
                                  })
                                } : undefined}
                              >
                                <i className="fas fa-play me-1"></i> Start
                              </a>
                            )}
                            {submissions?.length > 0 && (
                              <button
                                className="btn btn-sm"
                                style={{
                                  border: "1px solid #752a97",
                                  color: "#752a97",
                                  background: "transparent",
                                }}
                                onClick={() => openResult(item)}
                                title="View Results"
                              >
                                <i className="fas fa-chart-bar me-1"></i> Result
                                {submissions.length > 1 && (
                                  <span
                                    className="ms-1 badge"
                                    style={{ background: "#752a97", fontSize: 10 }}
                                  >
                                    {submissions.length}
                                  </span>
                                )}
                              </button>
                            )}
                            {!notExpired && (!submissions || submissions.length === 0) && (
                              <span
                                style={{
                                  fontSize: 12,
                                  color: "#9ca3af",
                                  background: "#f3f4f6",
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 20,
                                  padding: "3px 12px",
                                  fontWeight: 500,
                                }}
                              >
                                <i className="fas fa-clock me-1"></i> Expired
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Result Modal */}
      <Modal
        isOpen={resultModal}
        size="lg"
        centered
        toggle={() => setResultModal(false)}
      >
        <div className="modal-header pe-4">
          <h5 className="modal-title mt-0">
            <i className="fas fa-chart-bar me-2 text-purple"></i>
            {selectedItem?.test?.test_name} — Results
          </h5>
          <button
            type="button"
            onClick={() => setResultModal(false)}
            className="btn-modal-close"
          >
            <span aria-hidden="true" className="btn-modal-span">&times;</span>
          </button>
        </div>
        <div className="modal-body p-3">
          {selectedItem && (() => {
            const scores = selectedItem.submissions.map(s => s.score ?? 0)
            const best = Math.max(...scores)
            const avg = Math.round(scores.reduce((sum, s) => sum + s, 0) / scores.length)
            return (
              <>
                {/* Summary bar */}
                <div
                  className="d-flex align-items-center gap-3 mb-3 p-3"
                  style={{ background: "#faf5ff", borderRadius: 10, border: "1px solid #e9d5ff" }}
                >
                  {/* High Score ring */}
                  <div style={{ textAlign: "center", minWidth: 64 }}>
                    <div
                      style={{
                        width: 60, height: 60, borderRadius: "50%", margin: "0 auto 4px",
                        background: `conic-gradient(${ringColor(best)} ${best * 3.6}deg, #e9d5ff 0deg)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 44, height: 44, borderRadius: "50%", background: "#faf5ff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 800, fontSize: 13, color: scoreColor(best),
                        }}
                      >
                        {best}%
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#752a97", fontWeight: 600 }}>High Score</div>
                  </div>

                  {/* Avg Score ring */}
                  <div style={{ textAlign: "center", minWidth: 64 }}>
                    <div
                      style={{
                        width: 60, height: 60, borderRadius: "50%", margin: "0 auto 4px",
                        background: `conic-gradient(${ringColor(avg)} ${avg * 3.6}deg, #e9d5ff 0deg)`,
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 44, height: 44, borderRadius: "50%", background: "#faf5ff",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontWeight: 800, fontSize: 13, color: scoreColor(avg),
                        }}
                      >
                        {avg}%
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#752a97", fontWeight: 600 }}>Avg Score</div>
                  </div>

                  {/* Meta */}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#333", marginBottom: 6 }}>
                      {selectedItem.test.test_name}
                    </div>
                    <div className="d-flex gap-3" style={{ fontSize: 12, color: "#666" }}>
                      <span>
                        <i className="fas fa-redo me-1 text-purple"></i>
                        {selectedItem.submissions.length} Attempt{selectedItem.submissions.length !== 1 ? "s" : ""}
                      </span>
                      <span>
                        <i className="fas fa-check-circle me-1" style={{ color: "#16a34a" }}></i>
                        Pass: ≥{PASS_SCORE}%
                      </span>
                      <span>
                        <i className="fas fa-star me-1" style={{ color: "#d97706" }}></i>
                        Target: ≥{EXCELLENCE_SCORE}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Attempts table */}
                <table className="table mb-0" style={{ fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f5f0fa" }}>
                      <th style={{ width: 64, padding: "8px 10px" }} className="text-center">Attempt</th>
                      <th style={{ padding: "8px 10px" }}>Submitted On</th>
                      <th style={{ width: 80, padding: "8px 10px" }} className="text-center">Score</th>
                      <th style={{ width: 130, padding: "8px 10px" }} className="text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItem.submissions.map((sub, idx) => {
                      const score = sub.score ?? 0
                      const isBest = score === best && idx === selectedItem.submissions.findIndex(s => (s.score ?? 0) === best)
                      return (
                        <tr
                          key={sub.id}
                          style={{
                            background: scoreBg(score),
                            borderLeft: `3px solid ${scoreLeftBorder(score)}`,
                          }}
                        >
                          <td className="text-center" style={{ padding: "8px 10px" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 26,
                                height: 26,
                                borderRadius: "50%",
                                background: "#752a97",
                                color: "#fff",
                                fontWeight: 700,
                                fontSize: 12,
                              }}
                            >
                              {idx + 1}
                            </span>
                          </td>
                          <td style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                            {formatDateTime(sub.created_at)}
                            {isBest && selectedItem.submissions.length > 1 && (
                              <span
                                className="ms-2"
                                style={{
                                  fontSize: 10,
                                  background: "#fef9c3",
                                  color: "#854d0e",
                                  border: "1px solid #fde047",
                                  borderRadius: 10,
                                  padding: "1px 7px",
                                  fontWeight: 600,
                                }}
                              >
                                ★ Best
                              </span>
                            )}
                          </td>
                          <td className="text-center" style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                            <span style={{ fontWeight: 800, fontSize: 15, color: scoreColor(score) }}>
                              {score}%
                            </span>
                          </td>
                          <td className="text-center" style={{ padding: "8px 10px", verticalAlign: "middle" }}>
                            <span
                              style={{
                                display: "inline-block",
                                padding: "3px 10px",
                                borderRadius: 20,
                                fontSize: 11,
                                fontWeight: 600,
                                background: scoreBg(score),
                                border: `1px solid ${scoreBorder(score)}`,
                                color: scoreColor(score),
                              }}
                            >
                              {score >= EXCELLENCE_SCORE ? (
                                <><i className="fas fa-star me-1"></i>Excellent</>
                              ) : score >= PASS_SCORE ? (
                                <><i className="fas fa-check me-1"></i>Pass</>
                              ) : (
                                <><i className="fas fa-times me-1"></i>Failed</>
                              )}
                            </span>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </>
            )
          })()}
        </div>
      </Modal>
    </div>
  )
}

export default MyAssessment
